import { HomeMarqueeSectionPropType } from "@/components/organisms/Carousels/HomeMarqueeSection";
import { CreatorsGridProps } from "@/shared/data/home.v2.data";
import SectionHeader from "./shared/SectionHeader";

type Props = CreatorsGridProps & {
  // Kept on the props signature for call-site compatibility with the
  // legacy marquee; no longer rendered.
  marqueeProps: HomeMarqueeSectionPropType;
};

/**
 * CreatorsMarqueeV2 — "STORIES / Real Creators / Real Revenue"
 *
 * Layout: centered section header + CTA pair, then a single wide
 * creator-collage image bleeding off the bottom of the section. A
 * white→transparent→white gradient overlay softens the top and bottom
 * of the image so the section doesn't end abruptly against the next
 * section's background.
 */
const CreatorsMarqueeV2 = ({
  eyebrow,
  titleMuted,
  titleBright,
  title,
  subtitle,
  ctas,
  image,
  imageMobile,
}: Props) => {
  return (
    <section className="relative w-full overflow-hidden bg-white">
      <div className="container mx-auto px-4 pt-10 sm:px-6 md:px-8 md:pt-20 lg:px-12 lg:pt-24">
        <div className="mx-auto max-w-6xl">
        <SectionHeader
          eyebrow={eyebrow}
          titleMuted={titleMuted}
          titleBright={titleBright}
          title={title}
          subtitle={subtitle}
          ctas={ctas}
          variant="light-olive"
          align="center"
        />
      </div>
      </div>

      {/* Collage image — lives outside the container so it can bleed
          to the viewport edges. Min-height is set so the fade-gradient
          has enough vertical space to read as a soft transition. */}
      <div className="relative mt-10 w-full md:mt-14">
        {image || imageMobile ? (
          <>
            {/* Desktop collage */}
            {image ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={image}
                alt="Real creators on Graphy"
                className="hidden w-full md:block"
                loading="lazy"
                decoding="async"
              />
            ) : null}
            {/* Mobile collage — falls back to desktop image if no mobile variant */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={imageMobile ?? image}
              alt="Real creators on Graphy"
              className="block w-full md:hidden"
              loading="lazy"
              decoding="async"
            />
          </>
        ) : (
          // Placeholder box so the layout still reads before the final
          // asset lands. Height mirrors the referenced collage.
          <div className="h-[320px] w-full bg-gradient-to-br from-slateLight via-white to-slateLight md:h-[420px] lg:h-[480px]" />
        )}
      </div>
    </section>
  );
};

export default CreatorsMarqueeV2;
